import { Badge, Button, Modal, Select, Spinner } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PickRound, PickRoundMode } from '@warehouse/shared';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDate } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { flowsApi, pickingKeys, roundsApi } from '../api';
import { ModeBadge, RoundStatusBadge } from '../components/StatusBadge';

/** `YYYY-MM-DD` in the warehouse's timezone, for the date input's default. */
function todayInput(): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function PickRoundsPage(): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const navigate = useNavigate();
  const { state, setState } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });
  const [building, setBuilding] = useState(false);

  const rounds = useQuery({
    queryKey: pickingKeys.rounds(state),
    queryFn: () => roundsApi.list(state),
  });
  useLiveQuery(['pick-rounds', 'pick-tasks'], ['picking', 'rounds']);

  const columns: DataTableColumn<PickRound>[] = [
    {
      id: 'roundNo',
      header: t('rounds.roundNo'),
      sortField: 'roundNo',
      cell: (row) => <span className="font-mono text-sm">{row.roundNo}</span>,
    },
    {
      id: 'deliveryDate',
      header: t('rounds.deliveryDate'),
      sortField: 'deliveryDate',
      cell: (row) => formatDate(row.deliveryDate),
    },
    {
      id: 'status',
      header: t('rounds.progress'),
      sortField: 'status',
      cell: (row) => <RoundStatusBadge status={row.status} />,
    },
    { id: 'mode', header: t('rounds.mode'), cell: (row) => <ModeBadge mode={row.mode} /> },
    {
      id: 'flow',
      header: t('rounds.flow'),
      hideBelow: 'md',
      cell: (row) => row.flowName ?? '—',
    },
    {
      id: 'progress',
      header: t('rounds.tasks'),
      align: 'right',
      cell: (row) => (
        <span className="font-mono text-sm">
          {row.pickedCount + row.shortCount}/{row.taskCount}
          {row.shortCount > 0 ? (
            <Badge tone="danger" size="sm" className="ml-2">
              {row.shortCount}
            </Badge>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('rounds.title')}
        subtitle={t('rounds.subtitle')}
        actions={
          <Button
            icon={<Plus className="size-4" />}
            onClick={() => {
              setBuilding(true);
            }}
          >
            {t('rounds.new')}
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={rounds.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={rounds.data?.total ?? null}
        loading={rounds.isLoading}
        error={rounds.error}
        emptyTitle={t('rounds.empty')}
        emptyDescription={t('rounds.emptyDescription')}
        onRowClick={(row) => {
          void navigate(`/orders/picking/${row.id}`);
        }}
      />

      {building ? (
        <BuildRoundModal
          onClose={() => {
            setBuilding(false);
          }}
          onCreated={(round) => {
            setBuilding(false);
            void navigate(`/orders/picking/${round.id}`);
          }}
        />
      ) : null}
    </>
  );
}

function BuildRoundModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (round: PickRound) => void;
}): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();

  const [deliveryDate, setDeliveryDate] = useState(todayInput());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<PickRoundMode>('scanner');
  const [flowId, setFlowId] = useState<string>('');
  const [splitPerRestaurant, setSplitPerRestaurant] = useState(false);

  const candidates = useQuery({
    queryKey: pickingKeys.candidates(deliveryDate),
    queryFn: () => roundsApi.candidates(deliveryDate),
  });
  const flows = useQuery({ queryKey: pickingKeys.flows, queryFn: flowsApi.list });

  const build = useMutation({
    mutationFn: () =>
      roundsApi.build({
        deliveryDate,
        orderIds: [...selected],
        mode,
        splitPerRestaurant,
        ...(flowId ? { flowId } : {}),
      }),
    onSuccess: async (created) => {
      toastSuccess(t('rounds.created', { count: created.length }));
      await queryClient.invalidateQueries({ queryKey: ['picking', 'rounds'] });
      if (created[0]) onCreated(created[0]);
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  function toggleGroup(orderIds: string[], on: boolean): void {
    const next = new Set(selected);
    for (const id of orderIds) {
      if (on) next.add(id);
      else next.delete(id);
    }
    setSelected(next);
  }

  return (
    <Modal open onClose={onClose} title={t('rounds.build.title')} width={720}>
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('rounds.build.deliveryDate')}</span>
            <input
              type="date"
              className="rounded-lg border border-surface-border px-3 py-2"
              value={deliveryDate}
              onChange={(event) => {
                setDeliveryDate(event.target.value);
                setSelected(new Set());
              }}
            />
          </label>

          <Select
            label={t('rounds.build.mode')}
            value={mode}
            options={[
              { value: 'scanner', label: t('rounds.modes.scanner') },
              { value: 'paper', label: t('rounds.modes.paper') },
            ]}
            onChange={(event) => {
              setMode(event.target.value as PickRoundMode);
            }}
          />
        </div>

        <Select
          label={t('rounds.build.flow')}
          value={flowId}
          placeholder={t('rounds.build.useDefaultFlow')}
          options={(flows.data ?? []).map((flow) => ({
            value: flow.id,
            label: flow.isDefault ? `${flow.name} (${t('flows.defaultBadge')})` : flow.name,
          }))}
          onChange={(event) => {
            setFlowId(event.target.value);
          }}
        />

        <section>
          <h3 className="mb-2 text-sm font-semibold">{t('rounds.build.selectOrders')}</h3>
          {candidates.isLoading ? <Spinner /> : null}
          {!candidates.isLoading && (candidates.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-ink-muted">{t('rounds.build.noCandidates')}</p>
          ) : null}

          <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto" data-testid="round-candidates">
            {candidates.data?.map((group) => {
              const ids = group.orders.map((o) => o.orderId);
              const allOn = ids.every((id) => selected.has(id));
              return (
                <li
                  key={`${group.restaurantId}-${group.deliveryDate}`}
                  className="rounded-lg border border-surface-border p-3"
                >
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={allOn}
                      onChange={(event) => {
                        toggleGroup(ids, event.target.checked);
                      }}
                    />
                    {group.restaurantName}
                    <Badge tone="neutral" size="sm" className="ml-auto">
                      {t('rounds.build.orderCount', { count: group.orderCount })}
                    </Badge>
                    <Badge tone="neutral" size="sm">
                      {t('rounds.build.lineCount', { count: group.lineCount })}
                    </Badge>
                  </label>
                  <ul className="mt-2 flex flex-wrap gap-1.5 pl-6">
                    {group.orders.map((order) => (
                      <li key={order.orderId}>
                        <label className="flex items-center gap-1.5 text-xs text-ink-muted">
                          <input
                            type="checkbox"
                            checked={selected.has(order.orderId)}
                            onChange={(event) => {
                              toggleGroup([order.orderId], event.target.checked);
                            }}
                          />
                          <span className="font-mono">{order.orderNo}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </section>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={splitPerRestaurant}
            onChange={(event) => {
              setSplitPerRestaurant(event.target.checked);
            }}
          />
          <span>
            {t('rounds.build.splitPerRestaurant')}
            <span className="block text-xs text-ink-muted">{t('rounds.build.splitHint')}</span>
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            loading={build.isPending}
            disabled={selected.size === 0}
            onClick={() => {
              build.mutate();
            }}
          >
            {t('rounds.build.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
